import React, { useState, useEffect } from "react";
import axios from "axios";
import Comment from "./Comment";
import CommentForm from "./CommentForm";
import { useAuth } from "../../AuthContext";
import "./Comment.css";

const CommentSection = ({ postId }) => {
  const [comments, setComments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const { token } = useAuth();
  const [commentCount, setCommentCount] = useState(0);

  const fetchComments = async () => {
    if (!postId) return;

    setIsLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/blog/posts/${postId}/comments`
      );
      setComments(response.data);
      setCommentCount(response.data.length);
      setErrorMsg("");
    } catch (error) {
      setErrorMsg("Failed to fetch comments");
      console.error("Failed to fetch comments", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchComments();
  }, [postId]);

  const handleCommentSubmit = async (content) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/blog/posts/${postId}/comments`,
        { content },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setErrorMsg("");
      fetchComments(); // Refresh comments
      return true;
    } catch (error) {
      setErrorMsg(
        error.response?.data?.message ||
          "Failed to post comment. Are you logged in?"
      );
      console.error("Failed to post comment", error);
      return false;
    }
  };

  const handleUpdateComment = async (commentId, content) => {
    if (!content || !content.trim()) return;
    try {
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/blog/comments/${commentId}`,
        { content },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchComments();
    } catch (error) {
      console.error("Failed to update comment", error);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (window.confirm("Are you sure you want to delete this comment?")) {
      try {
        await axios.delete(
          `${process.env.REACT_APP_API_BASE_URL}/blog/comments/${commentId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        fetchComments();
      } catch (error) {
        console.error("Failed to delete comment", error);
      }
    }
  };

  return (
    <div className="comment-section">
      <div className="comment-section-header">
        <h3>
          Comments <span className="comment-count">({commentCount})</span>
        </h3>
      </div>

      <CommentForm postId={postId} onCommentSubmit={handleCommentSubmit} />

      {errorMsg && <div className="error-message">{errorMsg}</div>}

      <div className="comments-container">
        {isLoading ? (
          <div className="comments-loading">
            <div className="loading-spinner"></div>
            <p>Loading comments...</p>
          </div>
        ) : comments.length === 0 ? (
          <div className="no-comments">
            <p>No comments yet. Be the first to share your thoughts!</p>
          </div>
        ) : (
          <div className="comments-list">
            {comments.map((comment) => (
              <Comment
                key={comment._id}
                comment={comment}
                onUpdate={handleUpdateComment}
                onDelete={handleDeleteComment}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CommentSection;
